import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    heading: "1. High-Risk Investment",
    body: [
      "Cryptocurrency, stocks, foreign exchange and other financial markets involve a high degree of risk.",
      "Cryptocurrency in particular is highly volatile and speculative. Prices can move significantly and unexpectedly, and you may lose some or all of the capital you invest.",
      "You should never invest or allocate money that you cannot afford to lose.",
    ],
  },
  {
    heading: "2. Portfolio Management",
    body: [
      "Where The Syndicate provides portfolio management services, clients voluntarily authorise The Syndicate to manage and trade assets allocated to their portfolio.",
      "Portfolio management involves the risk of substantial financial loss. There is no guarantee that a portfolio will generate a profit or that the original capital will be preserved.",
      "The Syndicate does not guarantee any specific return, profit, performance or minimum portfolio value.",
    ],
  },
  {
    heading: "3. Loss of Capital",
    body: [
      "The client acknowledges and accepts that the capital allocated to portfolio management is fully at risk.",
      "The value of the portfolio may decrease substantially or become worthless as a result of market conditions, trading performance or other risks associated with digital assets and financial markets.",
      "The Syndicate will not reimburse, replace or compensate the client for losses resulting from ordinary market movements, unsuccessful trades, volatility, liquidity issues, slippage or other inherent investment and trading risks.",
      "A loss of the client's allocated capital does not create an obligation for The Syndicate to return or replace that capital.",
    ],
  },
  {
    heading: "4. Wallet & Seed Phrase Access",
    body: [
      "Where a client provides The Syndicate with a wallet seed phrase, private key or other wallet credentials, the client acknowledges that these credentials provide access to the assets held within that wallet.",
      "The client voluntarily provides such access for the purpose of enabling portfolio management and trading.",
      "The client acknowledges that providing wallet access carries additional risks, including cybersecurity risks, technical failures, unauthorised access, blockchain-related issues and other unforeseen events.",
    ],
  },
  {
    heading: "5. Trading Risks",
    body: [
      "Trading and portfolio management may be affected by factors including, but not limited to:",
    ],
    bullets: [
      "Market volatility",
      "Sudden price movements",
      "Liquidity conditions",
      "Slippage",
      "Failed or delayed transactions",
      "Blockchain congestion or network failures",
      "Smart-contract vulnerabilities",
      "Token or project failures",
      "Cybersecurity incidents",
      "Exchange or infrastructure failures",
      "Unforeseen market events",
      "Trading errors or losses",
    ],
  },
  {
    heading: "6. No Guaranteed Returns",
    body: [
      "Any historical performance, previous profits, win rates, testimonials, projections, examples or other performance information provided by The Syndicate are for informational purposes only.",
      "Past performance is not an indication or guarantee of future results.",
    ],
  },
  {
    heading: "7. Client Acknowledgement",
    body: [
      "By participating in The Syndicate's portfolio management service, the client confirms that they:",
    ],
    bullets: [
      "Understand that cryptocurrency and financial markets are high risk.",
      "Understand that they may lose 100% of their allocated capital.",
      "Understand that profits and returns are not guaranteed.",
      "Understand that the value of their portfolio may decrease substantially.",
      "Accept the risks associated with providing wallet access and/or seed phrase credentials.",
      "Have independently considered whether the service is suitable for them.",
      "Voluntarily accept the possibility of financial loss.",
    ],
  },
  {
    heading: "8. Limitation of Liability",
    body: [
      "To the maximum extent permitted by applicable law, The Syndicate shall not be responsible or liable for losses arising from market movements, trading performance or other inherent risks associated with cryptocurrency, stocks, foreign exchange or other financial markets.",
      "Nothing contained within this disclaimer shall exclude or limit any liability that cannot legally be excluded or limited.",
    ],
  },
  {
    heading: "9. Acceptance of Risk",
    body: [
      "BY PARTICIPATING IN THE SYNDICATE'S PORTFOLIO MANAGEMENT SERVICE, YOU ACKNOWLEDGE THAT TRADING AND INVESTING INVOLVES A SIGNIFICANT RISK OF LOSS AND THAT YOU MAY LOSE SOME OR ALL OF YOUR ALLOCATED CAPITAL. YOU ACCEPT THIS RISK AND UNDERSTAND THAT THE SYNDICATE DOES NOT GUARANTEE THE PRESERVATION OR RETURN OF YOUR CAPITAL.",
    ],
    emphasise: true,
  },
];

export default function RiskDisclosure() {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="risk-page">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-12 px-4 md:px-6">
        <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            LEGAL // RISK DISCLOSURE
          </p>
          <div className="mt-4 inline-flex items-center justify-center gap-3">
            <AlertTriangle size={26} className="text-white/70" strokeWidth={1.6} />
            <h1 className="font-display text-4xl md:text-6xl tracking-tight text-left">
              Risk Disclosure &amp; Portfolio Management Disclaimer
            </h1>
          </div>
          <p className="mt-6 font-mono-serif text-[10px] tracking-[0.3em] text-white/45">
            LAST UPDATED: 01 JANUARY 2026
          </p>
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-20">
        <div className="max-w-3xl mx-auto frame p-6 md:p-10 space-y-8 md:space-y-10">
          {SECTIONS.map((s, i) => (
            <div key={s.heading} data-testid={`risk-section-${i}`}>
              <h2 className="font-display text-2xl md:text-3xl">{s.heading}</h2>
              <div className={`mt-3 space-y-3 text-base md:text-[16px] leading-relaxed ${s.emphasise ? "text-white font-medium" : "text-white/70"}`}>
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
                {s.bullets && (
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 list-disc pl-5 marker:text-white/40">
                    {s.bullets.map((b, k) => (
                      <li key={k} className="text-white/70">{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-3xl md:text-4xl">Questions about the risk disclosure?</h2>
          <p className="mt-4 text-white/60">
            Reach out on Telegram — we reply quickly.
          </p>
          <a
            href="https://t.me/issyflash"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="risk-contact-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            DM @ISSYFLASH <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
